/**
 * @file sheets/bottom-sheet/bottom-sheet.tsx
 *
 * Standard (non-modal) Bottom Sheet following MD3 specification.
 *
 * - Slide-up animation with DEFAULT_SPATIAL_SPRING
 * - Drag-to-dismiss with seamless nested scroll handling
 * - Visual Viewport API for mobile keyboard resize
 * - WAI-ARIA: `role="dialog"`, `aria-modal="false"`
 * - SSR-safe portal rendering
 *
 * @see https://m3.material.io/components/sheets-bottom/overview
 */

import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils";
import { ScrollArea } from "../../scroll-area";
import { DEFAULT_SPATIAL_SPRING } from "../../shared/motion-tokens";
import { DragHandle } from "../shared/drag-handle";
import type { BaseSheetProps, BottomSheetSnapPoint } from "../shared/types";
import { DEFAULT_SHEET_BG, resolveDividers } from "../shared/utils";
import { useBottomSheet } from "./use-bottom-sheet";

// ─── Animation constants ───────────────────────────────────────────────────────

const SHEET_ANIM = {
	initial: { opacity: 0.6 },
	animate: { opacity: 1, transition: DEFAULT_SPATIAL_SPRING },
	exit: {
		opacity: 0,
		transition: { duration: 0.22, ease: "easeIn" as const },
	},
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the standard (non-modal) Bottom Sheet.
 */
export interface BottomSheetProps extends BaseSheetProps {
	/**
	 * Maximum height of the sheet.
	 * @default "90dvh"
	 */
	maxHeight?: string;

	/**
	 * Hide the drag handle visual indicator.
	 * @default false
	 */
	hideHandle?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ABSOLUTE_MAX_HEIGHT =
	"calc(100dvh - max(56px, env(safe-area-inset-top, 0px)))";

function resolveMaxHeight(
	maxHeight: string,
	snapPoints?: BottomSheetSnapPoint[],
): string {
	if (maxHeight === "90dvh" && snapPoints && snapPoints.length > 0)
		return ABSOLUTE_MAX_HEIGHT;
	return maxHeight;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Standard (non-modal) Bottom Sheet that slides up from the bottom edge.
 *
 * Does NOT trap focus or block background interaction. Use `BottomSheetModal`
 * for blocking behaviour.
 *
 * @example
 * ```tsx
 * <BottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <p>Sheet content</p>
 * </BottomSheet>
 * ```
 */
export const BottomSheet = React.forwardRef<HTMLDivElement, BottomSheetProps>(
	(
		{
			isOpen,
			onClose,
			children,
			className,
			maxHeight = "90dvh",
			hideHandle = false,
			portal = true,
			"aria-labelledby": ariaLabelledBy,
			"aria-label": ariaLabel,
			header,
			footer,
			divider,
			scrollAreaProps,
			snapPoints,
			defaultSnapPoint,
			onSnapChange,
			gesturesEnabled = true,
			containerColor,
			sheetMaxWidth,
		},
		ref,
	) => {
		const { scope, y, isContentScrollable, snapTo, cycleSnapPoint } =
			useBottomSheet({
				isOpen,
				onClose,
				snapPoints,
				defaultSnapPoint,
				onSnapChange,
				gesturesEnabled,
				isModal: false,
				maxHeight: resolveMaxHeight(maxHeight, snapPoints),
			});

		// Expose snapTo on the DOM node for programmatic control
		React.useEffect(() => {
			const node = (scope as unknown as { current: HTMLDivElement | null })
				.current;
			if (node) {
				(
					node as HTMLDivElement & {
						snapTo: (sp: BottomSheetSnapPoint) => void;
					}
				).snapTo = snapTo;
			}
		}, [snapTo, scope]);

		const [mounted, setMounted] = React.useState(false);
		React.useEffect(() => {
			setMounted(true);
		}, []);

		const resolvedDividers = React.useMemo(
			() => resolveDividers(divider),
			[divider],
		);
		const resolvedSheetMaxWidth = React.useMemo(() => {
			if (sheetMaxWidth === undefined)
				return "var(--m3-bottom-sheet-max-width, 640px)";
			if (typeof sheetMaxWidth === "number") return `${sheetMaxWidth}px`;
			return sheetMaxWidth;
		}, [sheetMaxWidth]);

		const mergedRef = React.useCallback(
			(node: HTMLDivElement | null) => {
				scope(node);
				if (typeof ref === "function") ref(node);
				else if (ref) ref.current = node;
			},
			[scope, ref],
		);

		const content = (
			<AnimatePresence>
				{isOpen && (
					<m.div
						ref={mergedRef}
						role="dialog"
						aria-modal="false"
						aria-label={ariaLabel}
						aria-labelledby={ariaLabelledBy}
						tabIndex={-1}
						className={cn(
							"fixed bottom-0 z-50",
							"left-0 right-0 mx-auto",
							"rounded-t-[28px]",
							!containerColor && DEFAULT_SHEET_BG,
							"flex flex-col overflow-hidden",
							"outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-inset",
							className,
						)}
						style={{
							backgroundColor: containerColor,
							// Container must span the full available viewport height so that
							// snap points above the default maxHeight (e.g. "full", "70%")
							// can reveal their content via the y (translateY) offset mechanism.
							height: "var(--sheet-vh, 100dvh)",
							maxHeight: ABSOLUTE_MAX_HEIGHT,
							maxWidth: resolvedSheetMaxWidth,
							y,
						}}
						{...SHEET_ANIM}
					>
						{!hideHandle && <DragHandle onCycle={cycleSnapPoint} />}

						{header && <div className="shrink-0 px-6 pb-4">{header}</div>}
						{header && resolvedDividers.header && (
							<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
						)}

						<ScrollArea
							type="hover"
							{...scrollAreaProps}
							className={cn(
								"flex-1 min-h-0 min-w-0",
								scrollAreaProps?.className,
							)}
							viewportClassName={cn(
								"px-6 pb-6 overscroll-contain",
								header ? "pt-4" : "",
								scrollAreaProps?.viewportClassName,
							)}
							viewportProps={{
								...scrollAreaProps?.viewportProps,
								style: {
									...scrollAreaProps?.viewportProps?.style,
									overflowY: isContentScrollable ? "auto" : "hidden",
								},
							}}
						>
							{children}
						</ScrollArea>

						{footer && resolvedDividers.footer && (
							<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
						)}
						{footer && <div className="shrink-0 px-6 pt-4 pb-6">{footer}</div>}
					</m.div>
				)}
			</AnimatePresence>
		);

		if (portal) {
			if (!mounted) return null;
			return createPortal(content, document.body);
		}

		return content;
	},
);

BottomSheet.displayName = "BottomSheet";
